import { useEffect, useRef } from "react";
import Auth from "@hooks/useAuth";
import RequestReservationContainer from "@hooks/useRequestReservation";
import { dayjs, toLocaleStr } from "@libs/utils/date";
import { InvalidParametersError } from "@libs/utils/dynamicURLParams/common";
import { findReservationDateOptionValue } from "./_reservationDateOptionValue";
import useQueryParams from "./_useQueryParams";
import { notSelectedValue } from "./index";
import type {
  RequestReservation_ReservationMatchOptionsByDateQuery,
  RequestReservation_ReservationVisitDatesQuery,
} from "./__generated__/queries";
import type { RequestReservationInputForm } from "./_useRequestReservationForm";
import type { MutableRefObject } from "react";
import type { UseFormReturn } from "react-hook-form";

const formatDateParam = (dateParam: string) =>
  dayjs(dateParam).format("YYYY-MM-DD");

const useFormReset = (
  methods: UseFormReturn<RequestReservationInputForm>,
  datesData: RequestReservation_ReservationVisitDatesQuery | undefined,
  matchOptionsData:
    | RequestReservation_ReservationMatchOptionsByDateQuery
    | undefined,
  selectedFrameId: string | undefined,
  selectedMatchId: string | undefined,
  selectedDate: string | undefined,
  isInitialized: boolean,
  setIsInitialized: (
    value: ((prevState: boolean) => boolean) | boolean
  ) => void,
  isMatchIdPendingRef: MutableRefObject<boolean>
) => {
  const { setValue, reset, getValues } = methods;
  const prevSelectedDateRef = useRef<string | undefined>(undefined);

  const { match: matchParam, date: dateParam } = useQueryParams();
  const {
    requestReservation: {
      reservationFrame,
      firstName,
      lastName,
      firstNameKana,
      lastNameKana,
      comesAt,
      supportingTeamId,
      personNum,
      phoneNumber,
      userMessage,
    },
  } = RequestReservationContainer.useContainer();
  const { fanstaUser } = Auth.useContainer();
  const currentUser = fanstaUser?.currentUser.user;
  const currentUserProfile = currentUser?.profile;

  const initializeForm = () => {
    // 確認画面から戻ってきた場合はその値をデフォルト値とする
    if (reservationFrame) {
      if (
        !personNum ||
        !comesAt ||
        !firstName ||
        !lastName ||
        !firstNameKana ||
        !lastNameKana ||
        !phoneNumber
      ) {
        throw new InvalidParametersError("他の項目が設定されていません");
      }
      reset({
        ...getValues(),
        frameId: reservationFrame.id.toString(),
        comesAt,
        matchId: reservationFrame.match
          ? reservationFrame.match.id.toString()
          : notSelectedValue,
        supportingTeamId: (supportingTeamId ?? "").toString(),
        personNum: personNum.toString(),
        firstName,
        lastName,
        firstNameKana,
        lastNameKana,
        phoneNumber,
        userMessage,
      });
      return;
    }
    const firstMatch = matchOptionsData?.reservationMatchOptionsByDate[0].match;
    const firstMatchId = firstMatch
      ? firstMatch.id.toString()
      : notSelectedValue;
    reset({
      ...getValues(),
      matchId: dateParam ? matchParam : firstMatchId,
      personNum: notSelectedValue,
      supportingTeamId: notSelectedValue,
      comesAt: notSelectedValue,
      firstName: currentUserProfile?.firstName ?? "",
      lastName: currentUserProfile?.lastName ?? "",
      firstNameKana: currentUserProfile?.firstNameKana ?? "",
      lastNameKana: currentUserProfile?.lastNameKana ?? "",
      phoneNumber: currentUser?.telWithHyphen ?? "",
    });
  };

  // 一番最初にReservationVisitDatesQueryが叩かれた後におこなう日付の初期化処理
  useEffect(() => {
    if (!datesData || isInitialized) {
      return;
    }
    const dates = datesData.reservationVisitDates ?? [];
    if (reservationFrame) {
      const date = toLocaleStr(reservationFrame.date, "ymdHyphen");
      reset({
        date: findReservationDateOptionValue(dates, date),
      });
      return;
    }
    const date = dateParam ? formatDateParam(dateParam) : notSelectedValue;
    reset({
      date: findReservationDateOptionValue(dates, date),
    });
  }, [datesData]);

  // 一番最初にReservationMatchOptionsByDateQueryが叩かれた後におこなう試合観戦、人数、応援チーム、予約者情報の初期化処理
  useEffect(() => {
    if (!matchOptionsData) {
      return;
    }
    if (!isInitialized) {
      initializeForm();
      // デフォルトの予約枠がなければ初期化処理はここで終わり
      if (!reservationFrame) {
        setIsInitialized(true);
      }
    }
  }, [matchOptionsData]);

  // 一番最初にReservationFramesByDateOrMatchQueryが叩かれた後におこなう初期化処理完了フラグの更新
  useEffect(() => {
    if (!selectedFrameId) {
      return;
    }
    if (!isInitialized) {
      // デフォルトの予約枠がある場合はここまできて初期化処理が終わる
      setIsInitialized(true);
    }
  }, [selectedFrameId]);

  /**
   * 日付変更時の状態リセット（page_view二重発火防止）
   *
   * 日付が変更されると新しいmatchOptionsDataがフェッチされるが、
   * その間にuseSyncQueryParamsが走ると古いmatchIdでpage_viewが発火する。
   * これを防ぐため、matchId確定までURL更新を待機させる。
   *
   * フロー:
   * 1. 日付変更検知 → isMatchIdPendingRef=true, matchId=undefined
   * 2. matchOptionsData取得 → matchId設定, isMatchIdPendingRef=false（下のuseEffectで実行）
   * 3. useSyncQueryParams実行 → 正しいmatchIdでURL更新
   */
  useEffect(() => {
    if (!isInitialized || prevSelectedDateRef.current === undefined) {
      prevSelectedDateRef.current = selectedDate;
      return;
    }

    if (
      prevSelectedDateRef.current !== selectedDate &&
      selectedDate !== undefined
    ) {
      isMatchIdPendingRef.current = true;
      setValue("matchId", undefined);
      setValue("frameId", undefined);
    }

    prevSelectedDateRef.current = selectedDate;
  }, [selectedDate, isInitialized, setValue, isMatchIdPendingRef]);

  /**
   * 日付変更後のmatchId自動選択（上記フローのステップ2）
   *
   * matchOptionsDataが更新されたら、一番上の試合を自動選択し、
   * isMatchIdPendingRef=falseにしてURL更新を許可する。
   */
  useEffect(() => {
    if (!matchOptionsData || !isInitialized) {
      return;
    }
    const firstMatch = matchOptionsData.reservationMatchOptionsByDate[0].match;
    setValue(
      "matchId",
      firstMatch ? firstMatch.id.toString() : notSelectedValue
    );
    setValue("frameId", undefined);
    // matchIdが確定したのでURL更新を許可
    isMatchIdPendingRef.current = false;
  }, [matchOptionsData, setValue, isMatchIdPendingRef]);

  // ユーザーが試合観戦オプションを選択し直すたびに応援チームと予約プランをリセットする処理
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    setValue("supportingTeamId", notSelectedValue);
    setValue("frameId", undefined);
  }, [selectedMatchId]);

  // ユーザーが予約プランを選択し直すたびに来店日時をリセットする処理
  useEffect(() => {
    if (!isInitialized) {
      return;
    }
    setValue("comesAt", notSelectedValue);
  }, [selectedFrameId]);
};

export default useFormReset;
